import {
  defineNuxtPlugin,
  useRequestEvent,
  useRoute,
  useRuntimeConfig,
  useState,
} from '#imports'

import type { Market } from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useSessionId } from '@backmarket/nuxt-module-identification/useSessionId'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import type { Features } from '@backmarket/nuxt-module-marketplace/features'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useIsE2ETestingEnvironment } from '@backmarket/nuxt-module-qa/useIsE2ETestingEnvironment'
import { getBrowserCookie } from '@backmarket/utils/cookies/getBrowserCookie'
import { isTestEnv } from '@backmarket/utils/env/isTestEnv'
import { toPosixLocale } from '@backmarket/utils/string/toPosixLocale'

import {
  init as initAmplitude,
  setUserProperties as setAmplitudeUserProperties,
} from '../amplitude'
import {
  FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
  USER_GROUP_NAME,
  USER_GROUP_VALUES,
} from '../amplitude/constants'
import { initBraze, setBrazeNotInitializing } from '../braze'
import { useDataConsent } from '../composables/useDataConsent'
import { useFeatureFlags } from '../composables/useFeatureFlags'
import { COOKIES } from '../constants'
import { setContext, setUserContext } from '../context'
import * as events from '../eventTrackers'
import { initGoogleTagManager } from '../google-tag-manager'
import { initNoodle } from '../noodle'
import { getInitialContext } from '../utils/getInitialContext'
import { initWebVitals } from '../webVitals'

import { getWebVitalsRouteName } from './utils/getWebVitalsRouteName'

function initializeTracking({
  market,
  features,
}: {
  market: Market
  features: Features
}) {
  const {
    public: { tracking: config },
  } = useRuntimeConfig()

  const route = useRoute()

  // Forwarded Amplitude Device ID from mobile apps to webviews
  // This allows to continuously track users between mobile apps and embedded webviews
  const forwardedAmplitudeDeviceId = useState<string | undefined>(
    FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
  ).value

  const visitorId = useVisitorId()
  const sessionId = useSessionId()
  const isE2ETestingEnvironment = useIsE2ETestingEnvironment()
  const currentLocale = useI18nLocale()

  const isOptInCookieNeeded = !config.countriesWithNoCookieOptIn.includes(
    market.countryCode,
  )

  const webVitalsRouteName = getWebVitalsRouteName(route, config.webVitals)

  initWebVitals({
    webVitalsRouteName,
    route,
    country: market.countryCode,
    webVitalsMonitoringEnabled: config.webVitals.isEnabled,
    logINPAttribution: config.webVitals.inpAttributionLoggingEnabled,
    logCLSAttribution: config.webVitals.clsAttributionLoggingEnabled,
    clsAttributionsDebugEnabled: config.webVitals.clsAttributionsDebugEnabled,
  })

  /* Provider initialization */

  /* Noodle */
  const shouldInitNoodle = config.noodle.isEnabled && !isE2ETestingEnvironment

  if (shouldInitNoodle) {
    const noodleUserContext = {
      country: market.countryCode,
      locale: toPosixLocale(currentLocale),
      sessionId,
      visitorId,
    }

    const noodleConfig = {
      environment: config.environment,
      service: config.service,
      url: config.noodle.url,
      version: config.version,
    }

    initNoodle(noodleUserContext, noodleConfig)
  }

  /* Amplitude */
  const isAmplitudeCountryCodeEnabled =
    config.amplitude.enabledCountryCodes.includes(market.countryCode)

  const hasUserEnabledAnalytics = isOptInCookieNeeded
    ? getBrowserCookie(COOKIES.analytics) === 'true'
    : true

  const shouldInitAmplitude =
    !isE2ETestingEnvironment &&
    config.amplitude.isEnabled &&
    isAmplitudeCountryCodeEnabled

  if (shouldInitAmplitude) {
    // If analytics are enabled, we init amplitude right here
    // if not, trackGDPR + setOptIn will trigger the init
    if (hasUserEnabledAnalytics)
      initAmplitude({ deviceId: forwardedAmplitudeDeviceId })
    // Skip the default NOTLOGGED set when inside a mobile webview: the native
    // app has already set user_type via its own Amplitude SDK and overwriting
    // it here would corrupt the shared user profile.
    if (!forwardedAmplitudeDeviceId) {
      setAmplitudeUserProperties({
        [USER_GROUP_NAME]: USER_GROUP_VALUES.NOT_LOGGED,
      })
    }
  }

  /* Braze */
  const shouldInitBraze = !isE2ETestingEnvironment && config.braze.isEnabled

  if (shouldInitBraze) {
    void initBraze({
      apiKey: config.braze.apiKey,
      isOptInCookieNeeded,
    })
  } else {
    setBrazeNotInitializing()
  }

  /* Google Tag Manager */
  initGoogleTagManager({
    isOptInCookieNeeded,
  })

  /* Context setting */
  const initialContext = getInitialContext(
    market,
    features,
    useExperiments(),
    useFeatureFlags(),
    config,
    visitorId,
    sessionId,
    { isDidomiEnabled: false },
  )
  setContext(initialContext)
}

export function createTrackingPlugin() {
  const marketplaceState = useMarketplace()

  const { isDidomiEnabled } = useDataConsent()
  if (isDidomiEnabled) return {}

  // Retrieving the forwarded Amplitude Device ID from the request header and pass it to the client through useState
  // This allows to continuously track users between mobile apps and embedded webviews
  if (process.server || isTestEnv()) {
    const amplitudeDeviceIdState = useState<string | undefined>(
      FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
    )
    const request = useRequestEvent()

    const forwardedAmplitudeDeviceId = request?.headers?.get(
      'bm-amplitude-device-id',
    )

    if (forwardedAmplitudeDeviceId) {
      amplitudeDeviceIdState.value = forwardedAmplitudeDeviceId
    }
  }

  if (process.client) {
    initializeTracking(marketplaceState)
  }

  return {
    provide: {
      tracking: {
        ...events,
        setContext,
        setUserContext,
      },
    },
  }
}

export default defineNuxtPlugin({
  name: 'tracking',
  dependsOn: ['logger'],
  setup: createTrackingPlugin,
})
